<template>
  <Layout class="layout--basic">
    <HeaderRoot title="Articles" :hasDocSort="true" />
    <div class="layout__wrapper">
      <v-wait for="Error Loading Articles">
        <template v-slot:waiting>
          <LoadingError />
        </template>
      </v-wait>
      <v-wait
        class="layout__wrapper-inner layout__wrapper-inner--small-gap container"
        for="Loading Articles"
      >
        <template v-slot:waiting>
          <loading-spinner />
        </template>
        <section class="article-categories">
          <CategoriesSelection v-if="tagsView?.hasTags" :view="tagsView" />
        </section>

        <section class="docs-listing docs-listing--articles">
          <ListDocs v-if="articlesView" :view="articlesView" />
        </section>
      </v-wait>
    </div>
  </Layout>
</template>

<script setup lang="ts">
import { onBeforeMount, ref } from 'vue'
import type { Raw } from 'vue'
import { useWait } from '@/app/composables/useWait'

import Layout from '@/app/components/Layout.vue'
import HeaderRoot from '@/app/components/header/Root.vue'

import { backend } from '@/services/backend'
import { Sentry } from '@/services/sentry'
import { ArticlesView } from '@/models/doc.article'
import CategoriesSelection from '@/app/components/listing/categories/CategoriesSelection.vue'
import ListDocs from '@/app/components/listing/ListDocs.vue'
import LoadingError from '@/components/ui/LoadingError.vue'
import { DocsTagsView } from '@/models/docs.tags'
import { ListView } from '@/models/docs.list'

const wait = useWait()

const articlesView = ref<Raw<ListView> | null>(null)
const tagsView = ref<Raw<DocsTagsView> | null>(null)

onBeforeMount(async () => {
  await loadArticles()
})

async function loadArticles(): Promise<void> {
  wait.start('Loading Articles')
  try {
    const docsUserDataPromise = backend.getDocsUserData()
    const articlesData = await backend.getArticles()

    const articles = await ArticlesView.createView(
      articlesData,
      undefined,
      true,
    )
    articlesView.value = new ListView('All Articles', articles)

    tagsView.value = new DocsTagsView(articlesData.tags, 'articles')
    wait.end('Loading Articles')

    // We deliberately set the user data _after_ ending the loading screen
    // because the user data is not essential for this page.
    articlesView.value.setUserData(docsUserDataPromise)
  } catch (error: any) {
    wait.start('Error Loading Articles')
    Sentry.captureException(error)
  }
}
</script>
